import {
  Portal,
  Stack,
  Text,
  ToastCloseTrigger,
  ToastDescription,
  ToastIndicator,
  ToastRoot as ToastRootBase,
  ToastTitle as ToastTitleBase,
  Toaster as ToasterBase,
} from '@chakra-ui/react'
import { createToaster as createToasterBase } from '@chakra-ui/react/toast'

import type { ReactNode } from 'react'

type ToastType = 'error' | 'success' | 'warning' | 'info'
type ToastOptions = {
  description?: ReactNode
  duration?: number
  title?: ReactNode
}

type RenderToast = ToastOptions & {
  type?: ToastType
}

type ToastStore = {
  error: (options: ToastOptions) => void
  info: (options: ToastOptions) => void
  success: (options: ToastOptions) => void
  warning: (options: ToastOptions) => void
}

const createToaster = createToasterBase as unknown as (properties: {
  gap: number
  max: number
  overlap: boolean
  placement: string
}) => ToastStore

const Toaster = ToasterBase as unknown as (properties: {
  children: (toast: RenderToast) => ReactNode
  toaster: ToastStore
}) => ReactNode

const ToastRoot = ToastRootBase as unknown as (properties: {
  alignItems: string
  bg: string
  border: string
  borderRadius: string
  boxShadow: string
  children: ReactNode
  color: string
  display: string
  gap: string
  maxW: string
  minW: string
  px: string
  py: string
  position: string
  unstyled: boolean
}) => ReactNode

const ToastTitle = ToastTitleBase as unknown as (properties: {
  children: ReactNode
  fontSize: string
  fontWeight: string
  unstyled: boolean
}) => ReactNode

const TOAST_TITLE: Record<ToastType, string> = {
  error: 'Error',
  success: 'Success',
  warning: 'Warning',
  info: 'Info',
}

const TOAST_GAP_PX = 12
const MAX_VISIBLE_TOASTS = 4
const DEFAULT_TOAST_DURATION_MS = 5000

const toaster = createToaster({
  gap: TOAST_GAP_PX,
  max: MAX_VISIBLE_TOASTS,
  overlap: false,
  placement: 'top-end',
})

export const showToast = (message: string, type: ToastType = 'error', duration = DEFAULT_TOAST_DURATION_MS) => {
  const options: ToastOptions = {
    description: message,
    duration,
    title: TOAST_TITLE[type],
  }

  switch (type) {
    case 'success': {
      toaster.success(options)
      break
    }
    case 'warning': {
      toaster.warning(options)
      break
    }
    case 'info': {
      toaster.info(options)
      break
    }
    default: {
      toaster.error(options)
    }
  }
}

export const AppToaster = () => {
  /* --- Section 6: Render --- */
  return (
    <Portal>
      <Toaster toaster={toaster}>
        {(toast: RenderToast) => (
          <ToastRoot
            bg="var(--theme-card-background)"
            border="1px solid var(--theme-accent-border)"
            borderRadius="[var(--radius-sm)]"
            boxShadow="var(--shadow-lg)"
            color="var(--theme-text-color)"
            display="flex"
            gap="12px"
            alignItems="flex-start"
            maxW="360px"
            minW="320px"
            px="16px"
            py="14px"
            position="relative"
            unstyled
          >
            <ToastIndicator
              boxSize="20px"
              color={toast.type === 'error' ? 'red.500' : undefined}
              flexShrink={0}
              mt="[2px]"
              unstyled
            />
            <Stack flex="[1]" gap="[2px]">
              <ToastTitle fontSize="[14px]" fontWeight="[700]" unstyled>
                {toast.title ?? TOAST_TITLE.info}
              </ToastTitle>
              {toast.description ? (
                <ToastDescription unstyled>
                  <Text fontSize="[13px]" lineHeight="[1.5]">
                    {toast.description}
                  </Text>
                </ToastDescription>
              ) : null}
            </Stack>
            <ToastCloseTrigger
              alignSelf="flex-start"
              color="var(--theme-text-color-muted)"
              display="inline-flex"
              flexShrink={0}
              h="[24px]"
              mt="[-2px]"
              w="[24px]"
              unstyled
            />
          </ToastRoot>
        )}
      </Toaster>
    </Portal>
  )
}
